import { useState, useCallback, useMemo, useEffect } from 'react';
import { useStore } from '../../../hooks/useStore';
import { useAuth } from '../../../hooks/useAuth';
import {
  getHarvestsByHtx, getFarmersByHtx, getFieldsByFarmer, getSeasonsByFarmer,
  getSeasonsByHtx, getContractsByHtx, addHarvest, reportHarvestActual, confirmHarvest,
  updateSeason, addEditHistory, updateRiskProfile,
  getRiskProfileByFarmer,
} from '../../../store';
import { showToast } from '../../../components/base/Toast';
import { LOW_YIELD_RATIO } from '../../../utils/status';
import { formatNumber } from '../../../utils/format';
import { EmptyState } from '../../../components/base/EmptyState';
import HarvestStats from './components/HarvestStats';
import HarvestFilters from './components/HarvestFilters';
import HarvestCard from './components/HarvestCard';
import HarvestFormModal from './components/HarvestFormModal';
import HarvestDetailModal from './components/HarvestDetailModal';
import HarvestConfirmModal from './components/HarvestConfirmModal';
import type { Harvest } from '../../../types';

export default function HtxHarvestPage() {
  useStore();
  const { user } = useAuth();
  const harvests = getHarvestsByHtx(user?.htxId);
  const farmers = getFarmersByHtx(user?.htxId);
  const allSeasons = getSeasonsByHtx(user?.htxId);
  const allContracts = getContractsByHtx(user?.htxId);

  const [filtered, setFiltered] = useState<Harvest[]>(harvests);
  const [formOpen, setFormOpen] = useState(false);
  const [editItem, setEditItem] = useState<Harvest | null>(null);
  const [detailItem, setDetailItem] = useState<Harvest | null>(null);
  const [confirmItem, setConfirmItem] = useState<Harvest | null>(null);

  const farmerOptions = useMemo(() => farmers.map((f) => ({ id: f.id, name: f.name })), [farmers]);
  const seasonOptions = useMemo(() => allSeasons.map((s) => ({ id: s.id, name: s.name })), [allSeasons]);

  // Sync filtered when harvests change
  useEffect(() => {
    if (filtered.length === 0 && harvests.length > 0) {
      setFiltered(harvests);
    }
  }, [harvests, filtered.length]);

  const getFieldName = (farmerId: string, id?: string) => getFieldsByFarmer(farmerId).find((f) => f.id === id)?.name || '-';
  const getSeasonName = (id?: string) => allSeasons.find((s) => s.id === id)?.name || '-';

  const getContractWarning = (harvest: Harvest) => {
    const contracts = allContracts.filter(
      (c) => c.farmerId === harvest.farmerId && c.seasonIds?.includes(harvest.seasonId || '')
    );
    const active = contracts.find((c) => c.status === 'Đang hiệu lực' || c.status === 'Sắp hết hạn');
    if (active) return `Ảnh hưởng đến hợp đồng ${active.soHopDong}`;
    return undefined;
  };

  const openCreate = () => {
    setEditItem(null);
    setFormOpen(true);
  };

  const openDetail = (item: Harvest) => {
    setDetailItem(item);
  };

  const openConfirm = (item: Harvest) => {
    setConfirmItem(item);
  };

  const handleSave = useCallback(async (data: Omit<Harvest, 'id' | 'maLanThuHoach'>) => {
    try {
      await addHarvest(data, user?.htxId);
      showToast('Đã tạo lần thu hoạch mới', 'success');
      setFormOpen(false);
      setEditItem(null);
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Tạo lần thu hoạch thất bại', 'error');
    }
  }, [user?.htxId]);

  const handleConfirmHarvest = useCallback(async (confirmedYield: number, qualityNote: string) => {
    if (!confirmItem || !user?.htxId) return;
    const h = confirmItem;
    try {
      // Real backend workflow: report-actual must land first (confirm() requires
      // status===PENDING_HTX_CONFIRM and an actualQuantity already set) — the
      // real LOW_YIELD/HTX_CONFIRMED split is then decided server-side.
      await reportHarvestActual(h.id, h.farmerId, confirmedYield, undefined, undefined, user.htxId);
      await confirmHarvest(h.id, h.farmerId, user.htxId, undefined, qualityNote || undefined);

      const season = allSeasons.find((s) => s.id === h.seasonId);
      // Against the harvest's own expected quantity (same record, same unit).
      // This used to divide by the season's yield ×1000, so a normal harvest
      // looked catastrophically short and its season was wrongly moved to
      // "Có rủi ro" — matching the backend's rule keeps the two verdicts equal.
      const isLowYield = h.soLuong > 0 && confirmedYield < h.soLuong * LOW_YIELD_RATIO;
      if (season) {
        // Best-effort — a farmer whose profile has since synced to their own
        // account (see EkycService.assertProfileEditAllowed) blocks HTX from
        // writing this season's status directly; the harvest confirm itself
        // still succeeds either way.
        updateSeason(season.id, { status: isLowYield ? 'Có rủi ro' : 'Đã thu hoạch' }, h.farmerId, user.htxId).catch(() => undefined);
      }

      // RiskProfile is out of scope for this migration (still the legacy mock
      // store) — kept exactly as before.
      if (isLowYield) {
        const risk = getRiskProfileByFarmer(h.farmerId);
        if (risk) {
          updateRiskProfile(risk.id, {
            mucDo: 'Cần chú ý',
            moTa: `${risk.moTa} | Thu hoạch ${h.maLanThuHoach} sản lượng thấp (${formatNumber(confirmedYield)} ${h.donVi}).`,
            ngayCapNhat: new Date().toISOString().split('T')[0],
          });
        } else {
          addEditHistory({
            entityType: 'RiskProfile',
            entityId: h.farmerId,
            farmerId: h.farmerId,
            truong: 'mucDo',
            giaTriCu: 'Chưa có',
            giaTriMoi: 'Cần chú ý',
            ngaySua: new Date().toISOString(),
            nguoiSua: 'HTX Hòa Phát',
          });
        }
      }

      showToast('Đã xác nhận thu hoạch', 'success');
      setConfirmItem(null);
      setDetailItem(null);
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Xác nhận thu hoạch thất bại', 'error');
    }
  }, [confirmItem, allSeasons, user?.htxId]);

  // "canhBao" (warning) now comes straight from the real backend's own
  // `Harvest.warning` field (set server-side by confirm()'s low-yield check)
  // — no client-side mutation needed to flag it, unlike the old mock.

  const displayList = filtered.length > 0 ? filtered : harvests;
  const activeList = displayList.filter((h) => !h.isDeleted);

  return (
    <div className="px-4 py-4 pb-4 space-y-4 max-w-[1200px] mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Quản lý thu hoạch</h2>
          <p className="text-sm text-slate-500 mt-1">
            Quản lý các lần thu hoạch theo mùa vụ, xác nhận sản lượng và theo dõi chất lượng.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="bg-agrida-700 hover:bg-agrida-800 text-white text-sm font-medium px-4 py-2.5 rounded-xl transition-colors whitespace-nowrap min-h-[44px] flex items-center gap-2 shrink-0"
        >
          <span className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line" /></span>
          Tạo lần thu hoạch
        </button>
      </div>

      {/* Stats */}
      <HarvestStats />

      {/* Filters */}
      <HarvestFilters
        harvests={harvests}
        farmers={farmerOptions}
        seasons={seasonOptions}
        onFilterChange={setFiltered}
      />

      {/* List */}
      {activeList.length === 0 ? (
        <EmptyState
          icon="ri-shopping-basket-line"
          title="Chưa có thu hoạch"
          description="Chưa có dữ liệu thu hoạch nào. Hãy tạo lần thu hoạch đầu tiên."
        />
      ) : (
        <div className="space-y-3">
          {activeList.map((h) => (
            <HarvestCard
              key={h.id}
              harvest={h}
              farmerName={farmers.find((f) => f.id === h.farmerId)?.name || '-'}
              seasonName={getSeasonName(h.seasonId)}
              fieldName={getFieldName(h.farmerId, h.fieldId)}
              onViewDetail={() => openDetail(h)}
              onConfirm={() => openConfirm(h)}
            />
          ))}
        </div>
      )}

      {/* Form modal */}
      {formOpen && (
        <HarvestFormModal
          editItem={editItem}
          onClose={() => { setFormOpen(false); setEditItem(null); }}
          onSave={handleSave}
        />
      )}

      {/* Detail modal */}
      {detailItem && (
        <HarvestDetailModal
          harvest={detailItem}
          farmerName={farmers.find((f) => f.id === detailItem.farmerId)?.name || '-'}
          seasonName={getSeasonName(detailItem.seasonId)}
          fieldName={getFieldName(detailItem.farmerId, detailItem.fieldId)}
          contractWarning={getContractWarning(detailItem)}
          linkedContract={allContracts.find((c) => c.id === detailItem.contractId)}
          onClose={() => setDetailItem(null)}
          onConfirm={() => {
            setDetailItem(null);
            setConfirmItem(detailItem);
          }}
        />
      )}

      {/* Confirm modal */}
      {confirmItem && (
        <HarvestConfirmModal
          harvest={confirmItem}
          onClose={() => setConfirmItem(null)}
          onConfirm={handleConfirmHarvest}
        />
      )}
    </div>
  );
}
